import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, Link } from 'react-router-dom'
import _ from 'lodash'
import { formatDate } from 'utils/dates'
import {
	Page,
	Layout,
	Button,
	Spinner,
	Text,
	Box,
	ButtonGroup,
} from '@shopify/polaris'
import NoDataMessage from 'components/NoDataMessage/NoDataMessage'
import Pagination from 'components/Pagination/Pagination'
import Modal from 'components/Modal/Modal'
import PageHelmet from 'components/PageHelmet'
import GoBackPageNavigation from '../../components/GoBackPageNavigation/GoBackPageNavigation'
import { deleteNews, fetchNews } from '../../redux/features/dashboard/dashboardSlice'
import useAppDispatch from '../../hooks/useAppDispatch'

const defaultLimit = 4

const News = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const dispatch = useAppDispatch()

	const user = useSelector((state) => state.user.user)
	const news = useSelector((state) => state.dashboard.news)
	const newsCount = useSelector((state) => state.dashboard.newsCount)
	const fetchingNews = useSelector((state) => state.dashboard.fetchingNews)

	const [pagination, setPagination] = useState({
		page: 1,
		pages: 1,
		perPage: defaultLimit,
		count: undefined,
	})
	const [query, setQuery] = useState({ limit: defaultLimit })
	const [actionDialogs, setActionDialogs] = useState({
		delete: { open: false },
		cellData: {},
	})

	useEffect(() => {
		document.title = 'News'
		dispatch(fetchNews({ limit: defaultLimit })).catch(_.noop)
	}, [dispatch])

	useEffect(() => {
		setPagination((prev) => {
			const count = newsCount || 0
			const pages =
				news && newsCount ? Math.ceil(newsCount / defaultLimit) || 1 : 1
			const next = {
				...prev,
				count,
				pages,
			}
			if (query && !query.offset) {
				next.page = 1
			}
			return next
		})
	}, [news, newsCount, query])

	const onSelectPage = useCallback(
		(inputPage) => {
			const pages = Math.ceil(newsCount / defaultLimit) || 1
			const page = Math.min(Math.max(inputPage, 1), pages)

			const nextQuery = {
				...query,
				offset: (page - 1) * defaultLimit,
				limit: defaultLimit,
			}

			setQuery(nextQuery)
			setPagination((prev) => ({
				...prev,
				page,
				pages,
			}))
			dispatch(fetchNews(nextQuery)).catch(_.noop)
		},
		[dispatch, newsCount, query]
	)

	const handleActionDialogsClose = useCallback((name) => {
		setActionDialogs((prev) => ({
			...prev,
			[name]: { ...prev[name], open: false },
			cellData: {},
		}))
	}, [])

	const handleActionDialogsOpen = useCallback((name, data = {}) => {
		setActionDialogs((prev) => ({
			...prev,
			[name]: { ...prev[name], open: true },
			cellData: data,
		}))
	}, [])

	const doDeleteNews = useCallback(() => {
		const id = _.get(actionDialogs, 'cellData.id')
		if (!id) return
		dispatch(deleteNews(id))
			.then(() => {
				dispatch(fetchNews(query)).catch(_.noop)
				handleActionDialogsClose('delete')
			})
			.catch(_.noop)
	}, [actionDialogs, dispatch, handleActionDialogsClose, query])

	const renderDeleteNewsDialog = () => {
		const onClose = () => handleActionDialogsClose('delete')

		return (
			<Modal
				title='Delete'
				visible={actionDialogs.delete.open}
				iconType='danger'
				content={
					<p style={{ maxWidth: '450px' }}>
						{t('news.sure')}{' '}
						{actionDialogs.cellData && `"${actionDialogs.cellData.title}"`}?
					</p>
				}
				footer={
					<ButtonGroup fullWidth>
						<Button onClick={onClose} size='large'>
							{t('billing.cancel')}
						</Button>
						<Button
							onClick={doDeleteNews}
							tone='critical'
							variant='plain'
							size='large'
						>
							{t('news.del')}
						</Button>
					</ButtonGroup>
				}
				onClose={onClose}
			/>
		)
	}

	return (
		<Page
			fullWidth
			separator
			title={
				<GoBackPageNavigation
					content={
						<Text variant='heading3xl' as='span'>
							{t('news.pageTitle')}
						</Text>
					}
				/>
			}
			subtitle={
				<Text variant='bodyLg' as='span' color='subdued'>
					{t('news.pageSubtitle')}
				</Text>
			}
			primaryAction={
				user.is_manager && (
					<Button variant='primary' onClick={() => navigate('/home/add-news')}>
						Add news
					</Button>
				)
			}
		>
			<PageHelmet title={t('news.pageTitle')} />

			<Layout>
				<Layout.Section>
					{fetchingNews ? (
						<Spinner />
					) : (
						<>
							<br />
							<div style={{ backgroundColor: 'white', padding: 30 }}>
								{!_.isEmpty(news) &&
									news.map((newsItem) => (
										<div key={newsItem.id} style={{ marginBottom: 20 }}>
											<div style={{ display: 'flex', flexWrap: 'nowrap' }}>
												<div>
													{newsItem?.imageSrc && (
														<img
															alt='news'
															width='180px'
															height='100%'
															style={{
																borderRadius: '8px',
																objectFit: 'cover',
																objectPosition: 'center',
															}}
															src={newsItem?.imageSrc || ''}
														/>
													)}
												</div>
												<div>
													<Box>
														<Text variant='bodyMd' color='critical'>
															{formatDate(newsItem.date)}
														</Text>
														<Text variant='headingLg' as='h2'>
															{newsItem.title}
														</Text>
														<Text>
															<span
																dangerouslySetInnerHTML={{
																	__html: newsItem.description,
																}}
															/>
														</Text>
														<Link
															className='Polaris-Link'
															to={`/home/news/${newsItem.id}`}
															style={{ textDecoration: 'none' }}
														>
															Read more
														</Link>
														{user.is_manager && (
															<div
																style={{
																	display: 'flex',
																	flexWrap: 'nowrap',
																	width: 200,
																}}
															>
																<Button
																	variant='plain'
																	onClick={() =>
																		handleActionDialogsOpen('delete', newsItem)
																	}
																>
																	<span style={{ color: 'red' }}>Delete</span>
																</Button>
															</div>
														)}
													</Box>
												</div>
											</div>
										</div>
									))}
								{_.isEmpty(news) && <NoDataMessage title='No articles yet' />}
							</div>
							{!_.isEmpty(news) && (
								<Box padding='4'>
									<Pagination
										total={pagination.count}
										current={pagination.page}
										pageSize={defaultLimit}
										onChange={onSelectPage}
									/>
								</Box>
							)}
						</>
					)}
				</Layout.Section>
			</Layout>
			{renderDeleteNewsDialog()}
		</Page>
	)
}

export default News
